import { zodResolver } from "@hookform/resolvers/zod";
import { registerUser } from "@stryker/core/api/accounts";
import type { RegisterPayload } from "@stryker/core/types/accounts";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";
import { z } from "zod";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";
import EyeIcon from "@/assets/icons/eye-open.svg?react";
import TickIcon from "@/assets/icons/tick.svg?react";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { hasSpecialSymbolRegex } from "@/constants/regex";
import useLocationQuery from "@/hooks/useLocationQuery";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/useAuthStore";
import { setAccessToken } from "@/utils/storageService";

const signupSchema = z.object({
  first_name: z.string().min(1, { message: "First name is required" }),
  last_name: z.string().min(1, { message: "Last name is required" }),
  email: z.string().email({ message: "This is not a valid email" }).nonempty("Email is required"),
  password: z
    .string()
    .min(1, { message: "Password is required" })
    .regex(hasSpecialSymbolRegex, "Must contain one special character"),
  confirmNewPassword: z.string().min(1, { message: "Confirm password is required" }),
});
type SignupSchema = z.infer<typeof signupSchema>;

export default function Signup() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChecks, setPasswordChecks] = useState({
    is8CharsOrMore: false,
    hasSpecialCharacter: false,
  });
  const { setIsAuthorized } = useAuthStore();

  const query = useLocationQuery();
  const registration_token = query.get("registration_token");
  const email = query.get("email");

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupSchema>({
    values: { email: email ?? "", first_name: "", last_name: "", password: "", confirmNewPassword: "" },
    resolver: zodResolver(signupSchema),
  });

  const {
    mutate,
    isPending,
    error: apiError,
  } = useMutation({
    mutationFn: (data: RegisterPayload) => registerUser(data),
    onError: (error: string) => error,
    onSuccess: (response) => {
      setAccessToken(response.tokens.access);
      setIsAuthorized(true);
    },
  });

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === "password") {
        const hasGoodLength = value.password && value.password.length >= 8;
        const hasSpecialSymbol = hasSpecialSymbolRegex.test(value.password ?? "");
        setPasswordChecks((prevState) => ({ ...prevState, is8CharsOrMore: Boolean(hasGoodLength) }));
        setPasswordChecks((prevState) => ({ ...prevState, hasSpecialCharacter: hasSpecialSymbol }));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  if (!registration_token || !email) {
    return <Navigate to="/login" />;
  }

  const onSignup = (data: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    confirmNewPassword: string;
  }) => {
    mutate({ ...data, registration_token });
  };

  return (
    <div className="w-[360px]">
      <div>
        <Typography className="mb-10" variant="display-lg" weight="semibold">
          Sign up
        </Typography>
        <Typography className="mb-10">Create your account now</Typography>
      </div>

      <form onSubmit={handleSubmit(onSignup)}>
        <div className="mb-2 flex flex-col gap-2">
          <TextInput
            type="text"
            label="First Name"
            placeholder="Enter your first name"
            error={errors.first_name?.message}
            {...register("first_name")}
          />
          <TextInput
            type="text"
            label="Last Name"
            placeholder="Enter your last name"
            error={errors.last_name?.message}
            {...register("last_name")}
          />
          <TextInput
            type="email"
            label="Email"
            // readOnly
            placeholder="Enter your email"
            error={errors.email?.message}
            {...register("email")}
          />
          <TextInput
            label="New Password"
            placeholder="Enter your new password"
            type={showPassword ? "text" : "password"}
            error={errors.password?.message}
            {...register("password")}
            endAdornment={
              showPassword ? (
                <EyeOffIcon onClick={() => setShowPassword(false)} />
              ) : (
                <EyeIcon onClick={() => setShowPassword(true)} />
              )
            }
          />
          <TextInput
            label="Confirm password"
            placeholder="Confirm new password"
            type={showConfirmPassword ? "text" : "password"}
            error={errors.confirmNewPassword?.message}
            {...register("confirmNewPassword")}
            endAdornment={
              showConfirmPassword ? (
                <EyeOffIcon onClick={() => setShowConfirmPassword(false)} />
              ) : (
                <EyeIcon onClick={() => setShowConfirmPassword(true)} />
              )
            }
          />
        </div>
        <div className="mt-5">
          <div className="mb-4 flex items-center gap-2">
            <div
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full ",
                passwordChecks.is8CharsOrMore ? "bg-success-100" : "bg-gray-100"
              )}
            >
              <TickIcon className={passwordChecks.is8CharsOrMore ? "stroke-success-700" : "stroke-gray-700"} />
            </div>
            <Typography variant="text-small" weight="medium">
              Must be at least 8 characters
            </Typography>
          </div>
          <div className="mb-4 flex items-center gap-2">
            <div
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full ",
                passwordChecks.hasSpecialCharacter ? "bg-success-100" : "bg-gray-100"
              )}
            >
              <TickIcon className={passwordChecks.is8CharsOrMore ? "stroke-success-700" : "stroke-gray-700"} />
            </div>
            <Typography variant="text-small" weight="medium">
              Must contain one special character
            </Typography>
          </div>
        </div>
        {apiError && (
          <Typography variant="text-small" weight="bold" className="text-error-500">
            {apiError}
          </Typography>
        )}
        <Button disabled={isPending} className="w-full" type="submit">
          Get start
        </Button>
      </form>

      <div className="mt-5 flex items-center justify-center gap-1">
        <Typography variant="text-small">Already have an account?</Typography>
        <Link to="/login">
          <Typography component="span" weight="semibold" className="text-primary-700">
            Log in
          </Typography>
        </Link>
      </div>
    </div>
  );
}
